import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { ICD10_SEARCH_MIN_LENGTH, Icd10BlockGroup, Icd10NodeNames, Icd10SearchState, Icd10TreeAction, groupIcd10Codes, matchIcd10Codes } from '@lib/insurance-packages/data-access';
import { TranslatePipe } from '@ngx-translate/core';

import { MatProgressSpinner } from '@angular/material/progress-spinner';

import { Icd10CodeRowComponent } from '../icd10-code-row/icd10-code-row.component';
import { Icd10DiagnosesTreeComponent } from '../icd10-diagnoses-tree/icd10-diagnoses-tree.component';

/**
 * A coverage's МКХ-10 codes: the block → category → code tree, or — while a search term is given — the codes
 * matching it as a flat list. Presentational: dictionary names, load and search states come in (see
 * `Icd10DictionaryStore`); opening a node, retrying, and edits are reported out. `action` is the tree's (a browsed list can only be removed from).
 */
@Component({
  selector: 'insurance-packages-icd10-diagnoses-browser',
  standalone: true,
  imports: [MatProgressSpinner, TranslatePipe, Icd10CodeRowComponent, Icd10DiagnosesTreeComponent],
  templateUrl: './icd10-diagnoses-browser.component.html',
  host: { class: 'flex min-h-0 flex-col' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Icd10DiagnosesBrowserComponent {
  readonly codes = input.required<string[]>();
  readonly search = input<string>('');
  readonly searchState = input<Icd10SearchState | null>(null);
  readonly names = input<Record<string, string>>({});
  readonly nodes = input<Record<string, Icd10NodeNames>>({});
  readonly blockCodes = input<string[]>([]);
  readonly blocksState = input<Icd10NodeNames | undefined>(undefined);
  readonly fillHeight = input<boolean>(false);
  readonly action = input<Extract<Icd10TreeAction, 'remove'> | null>(null);
  readonly selectedCodes = input<ReadonlySet<string>>(new Set());

  readonly blockOpened = output<Icd10BlockGroup>();
  readonly categoryOpened = output<string>();
  readonly retryGroups = output<void>();
  readonly retrySearch = output<string>();
  readonly remove = output<string[]>();
  readonly selectedCodesChange = output<ReadonlySet<string>>();

  protected readonly groups = computed(() => groupIcd10Codes(this.codes(), this.blockCodes()));

  private readonly term = computed(() => this.search().trim());
  protected readonly searching = computed(() => this.term().length >= ICD10_SEARCH_MIN_LENGTH);
  protected readonly hits = computed(() => {
    const results = this.searchState()?.results ?? {};
    return matchIcd10Codes(this.codes(), this.term(), results).map(code => ({ code, name: results[code] || this.names()[code] || '' }));
  });

  protected readonly scrollClass = computed(() => (this.fillHeight() ? 'min-h-0 overflow-auto text-sm' : 'min-h-0 max-h-[60vh] overflow-auto text-sm'));

  /** A failed node is retried by opening it again: blocks by their group, anything else as a category. */
  protected retryNode(key: string): void {
    const group = this.groups().find(item => item.key === key);
    if (group) this.blockOpened.emit(group);
    else this.categoryOpened.emit(key);
  }

  protected toggleSelected(code: string): void {
    const next = new Set(this.selectedCodes());
    if (!next.delete(code)) next.add(code);
    this.selectedCodesChange.emit(next);
  }
}
